import { COLORS } from "../core/constants.ts";
import { t } from "../utils/i18n.ts";
import { UI_FONT_STACK, roleFontSize, withUiScale } from "./uiTokens.ts";

/**
 * SpawnAlert — Full-screen warning overlay when a new spawn point activates.
 * Styled like a "Boss Alert" with large text and red/amber pulsing.
 * Layer 4 (HTML/CSS).
 */
export class SpawnAlert {
  private overlay: HTMLDivElement;
  private onDismiss: (() => void) | null = null;
  private autoTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(parent: HTMLElement) {
    this.overlay = document.createElement("div");
    this.overlay.id = "spawn-alert-overlay";
    this.overlay.style.display = "none";
    this.overlay.addEventListener("click", () => this.dismiss());
    parent.appendChild(this.overlay);
    this.injectStyles();
  }

  show(onDismiss: () => void): void {
    this.onDismiss = onDismiss;
    this.overlay.innerHTML = "";

    const container = document.createElement("div");
    container.className = "spawn-alert-content";

    const icon = document.createElement("div");
    icon.className = "spawn-alert-icon";
    icon.textContent = t("spawn.icon");
    container.appendChild(icon);

    const line1 = document.createElement("div");
    line1.className = "spawn-alert-line1";
    line1.textContent = t("spawn.alert.line1");
    container.appendChild(line1);

    const line2 = document.createElement("div");
    line2.className = "spawn-alert-line2";
    line2.textContent = t("spawn.alert.line2");
    container.appendChild(line2);

    const hint = document.createElement("div");
    hint.className = "spawn-alert-hint";
    hint.textContent = t("spawn.alert.dismiss");
    container.appendChild(hint);

    this.overlay.appendChild(container);
    this.overlay.style.display = "flex";
    this.overlay.classList.add("visible");

    // Auto-dismiss after 4 seconds
    this.autoTimer = setTimeout(() => this.dismiss(), 4000);
  }

  /**
   * Force-dismiss without firing the onDismiss callback. Used on run reset so a
   * mid-alert restart can't leave this pointer-events overlay (or a stale
   * callback) covering the fresh run.
   */
  hide(): void {
    if (this.autoTimer) {
      clearTimeout(this.autoTimer);
      this.autoTimer = null;
    }
    this.onDismiss = null;
    this.overlay.classList.remove("visible");
    this.overlay.style.display = "none";
  }

  private dismiss(): void {
    if (this.autoTimer) {
      clearTimeout(this.autoTimer);
      this.autoTimer = null;
    }
    this.overlay.classList.remove("visible");
    this.overlay.style.display = "none";
    if (this.onDismiss) {
      const cb = this.onDismiss;
      this.onDismiss = null;
      cb();
    }
  }

  private injectStyles(): void {
    if (document.getElementById("spawn-alert-styles")) return;
    const style = document.createElement("style");
    style.id = "spawn-alert-styles";
    style.textContent = `
      #spawn-alert-overlay {
        /* S183/BL-201 — absolute (was fixed): mounts in #ui-layer inside the
           1600x900 stage, so inset:0 fills the IDENTICAL frame as every screen. */
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba(8, 12, 24, 0.85);
        z-index: 200;
        cursor: pointer;
        animation: spawn-alert-fadein 0.3s ease-out;
      }
      @keyframes spawn-alert-fadein {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      .spawn-alert-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 12px;
        padding: 40px 60px;
        border: 2px solid ${COLORS.ORACLE_RED};
        background: rgba(255, 45, 85, 0.05);
        box-shadow: 0 0 40px ${COLORS.ORACLE_RED}40, inset 0 0 20px ${COLORS.ORACLE_RED}20;
        animation: spawn-alert-pulse 1.5s ease-in-out infinite;
      }
      @keyframes spawn-alert-pulse {
        0%, 100% { box-shadow: 0 0 40px ${COLORS.ORACLE_RED}40, inset 0 0 20px ${COLORS.ORACLE_RED}20; }
        50% { box-shadow: 0 0 60px ${COLORS.ORACLE_RED}80, inset 0 0 30px ${COLORS.ORACLE_RED}40; }
      }
      .spawn-alert-icon {
        font-size: ${withUiScale(64)};
        color: ${COLORS.AMBER_WARN};
        text-shadow: 0 0 20px ${COLORS.AMBER_WARN};
        animation: spawn-alert-blink 0.6s step-end infinite;
      }
      @keyframes spawn-alert-blink {
        0%, 49% { opacity: 1; }
        50%, 100% { opacity: 0.3; }
      }
      .spawn-alert-line1 {
        font-family: ${UI_FONT_STACK};
        font-size: ${roleFontSize("title")};
        color: ${COLORS.ORACLE_RED};
        text-shadow: 0 0 10px ${COLORS.ORACLE_RED};
        letter-spacing: 2px;
        text-align: center;
        text-transform: uppercase;
      }
      .spawn-alert-line2 {
        font-family: ${UI_FONT_STACK};
        font-size: ${roleFontSize("heading")};
        color: ${COLORS.AMBER_WARN};
        text-shadow: 0 0 8px ${COLORS.AMBER_WARN};
        letter-spacing: 3px;
        text-align: center;
        text-transform: uppercase;
      }
      .spawn-alert-hint {
        margin-top: 16px;
        font-family: ${UI_FONT_STACK};
        font-size: ${roleFontSize("micro")};
        color: ${COLORS.TEXT_SECONDARY};
        letter-spacing: 2px;
        animation: wave-blink 1.2s ease-in-out infinite;
      }
      /* BL-195 motion-a11y / §B / §I: the spawn alert is a punchy EVENT beat
         (a transient 4s overlay — kept punchy for full-motion per the owner
         steer), but it ran THREE infinite pulses (box-shadow / icon blink / hint
         blink) with no reduced-motion seam and the fade-in is the only entrance.
         Under prefers-reduced-motion the strobes are killed and the overlay snaps
         in instantly — the WARNING is fully shown (red frame, both lines, the
         dismiss hint, all legible), only the motion is dropped (photosensitivity-
         safe). Closes the net's reduced-motion gap on this surface. */
      @media (prefers-reduced-motion: reduce) {
        #spawn-alert-overlay,
        .spawn-alert-content,
        .spawn-alert-icon,
        .spawn-alert-hint { animation: none; }
      }
    `;
    document.head.appendChild(style);
  }
}
